/*
 * pdp.js — modular product page behavior (pdp-* sections).
 *
 * Gate compatibility contract (rc-location-picker.js):
 *  - All cart mutations flow from the single ATC submit/click, so the delivery
 *    gate's document-level CAPTURE listeners always run first and can block us.
 *  - We bind on the form/button elements only (never document capture) and we
 *    bail out when event.defaultPrevented is already set.
 *
 * Cart integration (ajax-cart.js / timber.js):
 *  - Adds via POST /cart/add.js with items[] (main product + selected extras),
 *    then refreshes the theme minicart via ajaxCart.load() and opens the
 *    drawer via timber.RightDrawer.open(). Falls back to redirecting to /cart.
 *  - The ATC button deliberately does NOT carry .enj-add-to-cart-btn — that
 *    class makes ajaxCart.formOverride do a second (duplicate) add.
 */
(function () {
  "use strict";

  if (window.__pdpInit) return; // deferred script may be included per-section
  window.__pdpInit = true;

  var money =
    typeof Shopify !== "undefined" && Shopify.formatMoney
      ? function (cents) {
          return Shopify.formatMoney(cents, window.money_format || "${{amount}}");
        }
      : function (cents) {
          return "$" + (cents / 100).toFixed(2);
        };

  function initSection(root) {
    if (!root || root.dataset.pdpReady) return;
    root.dataset.pdpReady = "1";
    if (root.classList.contains("pdp-main")) initMain(root);
    if (root.classList.contains("pdp-similar")) initSimilar(root);
  }

  /* ---------------- pdp-main ---------------- */

  function initMain(root) {
    var dataEl = root.querySelector("[data-pdp-product]");
    var product = null;
    try {
      product = dataEl ? JSON.parse(dataEl.textContent) : null;
    } catch (e) {
      product = null;
    }

    initGallery(root);
    initQty(root);
    if (product) initVariants(root, product);
    initExtras(root, product);
    initVaseOffer(root);
    initCountdown(root);
    initLocationChip(root);
    initStickyBar(root);
    initAtc(root, product);
  }

  /* gallery: scroll-snap track + thumbs + arrows */
  function initGallery(root) {
    var track = root.querySelector(".pdp-gallery-track");
    if (!track) return;
    var slides = track.querySelectorAll(".pdp-media");
    var thumbs = root.querySelectorAll(".pdp-thumb");
    var moreButton = root.querySelector("[data-pdp-thumbs-more]");
    var zoom = root.querySelector("[data-pdp-zoom]");
    var zoomImage = zoom && zoom.querySelector("[data-pdp-zoom-image]");
    var zoomDialog = zoom && zoom.querySelector(".pdp-zoom-dialog");
    var zoomTrigger = null;
    var previousOverflow = "";

    // Escape gallery/section stacking contexts so the lightbox covers all PDP UI.
    if (zoom && zoom.parentNode !== document.body) document.body.appendChild(zoom);

    function revealThumbs() {
      thumbs.forEach(function (thumb) {
        thumb.hidden = false;
      });
      if (moreButton) {
        moreButton.hidden = true;
        moreButton.setAttribute("aria-expanded", "true");
      }
    }

    function closeZoom() {
      if (!zoom || zoom.hidden) return;
      zoom.hidden = true;
      zoom.setAttribute("aria-hidden", "true");
      if (zoomDialog) zoomDialog.classList.remove("is-magnified");
      document.documentElement.style.overflow = previousOverflow;
      if (zoomTrigger) zoomTrigger.focus();
    }

    function openZoom(trigger) {
      if (!zoom || !zoomImage || !trigger) return;
      zoomTrigger = trigger;
      zoomImage.src = trigger.dataset.pdpZoomSrc || trigger.currentSrc || trigger.src;
      zoomImage.alt = trigger.alt || "Product image";
      previousOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = "hidden";
      zoom.hidden = false;
      zoom.setAttribute("aria-hidden", "false");
      var closeButton = zoom.querySelector(".pdp-zoom-close");
      if (closeButton) closeButton.focus();
    }

    function goTo(index) {
      var slide = slides[index];
      if (!slide) return;
      track.scrollTo({ left: slide.offsetLeft, behavior: "smooth" });
    }

    thumbs.forEach(function (thumb, i) {
      thumb.addEventListener("click", function () {
        goTo(i);
      });
    });

    if (moreButton) moreButton.addEventListener("click", revealThumbs);

    root.querySelectorAll("[data-pdp-zoom-src]").forEach(function (image) {
      image.addEventListener("click", function () {
        openZoom(image);
      });
    });

    if (zoom) {
      zoom.querySelectorAll("[data-pdp-zoom-close]").forEach(function (button) {
        button.addEventListener("click", closeZoom);
      });
      if (zoomImage && zoomDialog) {
        zoomImage.addEventListener("click", function () {
          zoomDialog.classList.toggle("is-magnified");
        });
      }
      document.addEventListener("keydown", function (event) {
        if (event.key === "Escape" && !zoom.hidden) closeZoom();
      });
    }

    root.querySelectorAll(".pdp-gallery-nav").forEach(function (btn) {
      btn.addEventListener("click", function () {
        var current = Math.round(track.scrollLeft / track.clientWidth);
        goTo(current + (btn.dataset.dir === "next" ? 1 : -1));
      });
    });

    // Active thumb follows scroll position.
    var scrollTimer = null;
    track.addEventListener("scroll", function () {
      if (scrollTimer) return;
      scrollTimer = setTimeout(function () {
        scrollTimer = null;
        var current = Math.round(track.scrollLeft / track.clientWidth);
        thumbs.forEach(function (t, i) {
          t.classList.toggle("is-active", i === current);
        });
        if (thumbs[current] && thumbs[current].hidden) revealThumbs();
      }, 80);
    });

    root.__pdpGoTo = goTo;
  }

  function initQty(root) {
    var qty = root.querySelector(".pdp-qty input");
    if (!qty) return;

    function normalize() {
      qty.value = Math.max(1, parseInt(qty.value, 10) || 1);
    }

    root.querySelectorAll(".pdp-qty button").forEach(function (btn) {
      btn.addEventListener("click", function (event) {
        if (event.defaultPrevented) return; // delivery gate intervened
        var next = (parseInt(qty.value, 10) || 1) + (btn.dataset.dir === "up" ? 1 : -1);
        qty.value = Math.max(1, next);
        qty.dispatchEvent(new Event("change", { bubbles: true }));
      });
    });
    qty.addEventListener("change", normalize);
    qty.addEventListener("blur", normalize);
  }

  /* variant pills: radio groups -> matching variant -> price/state/media */
  function initVariants(root, product) {
    var pills = root.querySelectorAll(".pdp-pill input");
    var idInput = root.querySelector('input[name="id"]');
    if (!pills.length || !idInput) return;

    function selectedOptions() {
      var opts = [];
      root.querySelectorAll(".pdp-pills").forEach(function (group) {
        var checked = group.querySelector("input:checked");
        opts.push(checked ? checked.value : null);
      });
      return opts;
    }

    function findVariant(opts) {
      return product.variants.filter(function (v) {
        return opts.every(function (value, i) {
          return value === null || v.options[i] === value;
        });
      })[0];
    }

    function bundleUnits(variant) {
      if (!variant || !variant.options) return null;
      for (var i = 0; i < variant.options.length; i += 1) {
        var match = String(variant.options[i]).match(/(?:^|\s)(\d+(?:\.\d+)?)(?=\s|$)/);
        if (match) {
          var units = parseFloat(match[1]);
          if (Number.isFinite(units) && units > 0) return units;
        }
      }
      return null;
    }

    function updateBulkSaving(variant) {
      var savingEl = root.querySelector("[data-pdp-bulk-saving]");
      if (!savingEl) return;

      var baselines = product.variants
        .map(function (candidate) {
          return { variant: candidate, units: bundleUnits(candidate) };
        })
        .filter(function (candidate) {
          return candidate.units !== null;
        })
        .sort(function (a, b) {
          return a.units - b.units;
        });
      var selectedUnits = bundleUnits(variant);
      var base = baselines[0];

      if (!base || selectedUnits === null || selectedUnits <= base.units) {
        savingEl.hidden = true;
        savingEl.textContent = "";
        return;
      }

      var fullPrice = Math.round((base.variant.price / base.units) * selectedUnits);
      var saving = fullPrice - variant.price;
      savingEl.hidden = saving <= 0;
      savingEl.textContent = saving > 0 ? "Save " + money(saving) : "";
    }

    function apply() {
      var variant = findVariant(selectedOptions());
      var atc = root.querySelector(".pdp-atc");
      var priceEl = root.querySelector(".pdp-price-current");
      var compareEl = root.querySelector(".pdp-price-compare");
      var stickyPrice = root.querySelector(".pdp-sticky-price");

      root.querySelectorAll(".pdp-option-label span").forEach(function (label, i) {
        var checked = root.querySelectorAll(".pdp-pills")[i].querySelector("input:checked");
        if (checked) label.textContent = checked.value;
      });

      if (!variant) {
        if (atc) {
          atc.disabled = true;
          atc.textContent = atc.dataset.textUnavailable || "Unavailable";
        }
        return;
      }

      idInput.value = variant.id;
      if (priceEl) priceEl.textContent = money(variant.price);
      if (stickyPrice) stickyPrice.textContent = money(variant.price);
      if (compareEl) {
        var onSale = variant.compare_at_price > variant.price;
        compareEl.hidden = !onSale;
        if (onSale) compareEl.textContent = money(variant.compare_at_price);
      }
      updateBulkSaving(variant);
      if (atc) {
        atc.disabled = !variant.available;
        atc.textContent = variant.available
          ? atc.dataset.textAdd || "Add to cart"
          : atc.dataset.textSoldout || "Sold out";
      }
      if (variant.featured_media && root.__pdpGoTo) {
        var slides = root.querySelectorAll(".pdp-media");
        slides.forEach(function (slide, i) {
          if (String(slide.dataset.mediaId) === String(variant.featured_media.id)) {
            root.__pdpGoTo(i);
          }
        });
      }
    }

    pills.forEach(function (input) {
      input.addEventListener("change", apply);
    });
    apply();
  }

  /* extras: inline choices + accessible full-range dialog */
  function initExtras(root, product) {
    var inputSelector = "[data-pdp-extra-input]";
    var modal = root.querySelector("[data-pdp-extras-modal]");
    var opener = root.querySelector("[data-pdp-extras-open]");
    var selectedTray = root.querySelector("[data-pdp-extras-selected]");
    var priceEl = root.querySelector(".pdp-price-current");
    var giftCountEl = root.querySelector("[data-pdp-price-gifts]");
    var lastFocus = null;
    var previousOverflow = "";

    function syncVariant(variantId, checked) {
      if (!variantId) return;
      root.querySelectorAll(inputSelector).forEach(function (input) {
        if (String(input.value) !== String(variantId)) return;
        input.checked = checked;
        var card = input.closest(".pdp-extra, [data-pdp-extra]");
        if (card) card.classList.toggle("is-selected", checked);
      });
    }

    function selectedVariants() {
      var selected = {};
      root.querySelectorAll(inputSelector).forEach(function (input) {
        if (!input.checked || selected[input.value]) return;
        selected[input.value] = input;
      });
      return selected;
    }

    function renderSelectedTray() {
      if (!selectedTray || !modal) return;
      selectedTray.innerHTML = "";
      var inlineIds = {};
      root.querySelectorAll(".pdp-extras-grid [data-pdp-extra-input]").forEach(function (input) {
        inlineIds[input.value] = true;
      });
      Object.keys(selectedVariants()).forEach(function (variantId) {
        if (inlineIds[variantId]) return;
        var source = modal.querySelector('[data-pdp-extra-input][value="' + variantId + '"]');
        var sourceCard = source && source.closest(".pdp-extra, [data-pdp-extra]");
        if (!sourceCard) return;
        var card = sourceCard.cloneNode(true);
        card.classList.remove("pdp-extra--popup");
        card.classList.add("pdp-extra--selected", "is-selected");
        var input = card.querySelector(inputSelector);
        if (input) input.checked = true;
        selectedTray.appendChild(card);
      });
      selectedTray.hidden = !selectedTray.children.length;
    }

    function updatePriceSummary() {
      var selected = selectedVariants();
      var extraTotal = Object.keys(selected).reduce(function (total, variantId) {
        return total + (parseInt(selected[variantId].dataset.extraPrice, 10) || 0);
      }, 0);
      var selectedVase = root.querySelector("[data-pdp-vase-input]:checked");
      if (selectedVase) extraTotal += parseInt(selectedVase.dataset.extraPrice, 10) || 0;
      var idInput = root.querySelector('input[name="id"]');
      var currentId = parseInt(idInput && idInput.value, 10);
      var variant = product && product.variants
        ? product.variants.filter(function (item) { return item.id === currentId; })[0]
        : null;
      if (priceEl && variant) priceEl.textContent = money(variant.price + extraTotal);
      if (giftCountEl) {
        var count = Object.keys(selected).length + (selectedVase ? 1 : 0);
        giftCountEl.textContent = "includes " + count + " extra" + (count === 1 ? "" : "s");
        giftCountEl.hidden = count === 0;
      }
    }

    root.addEventListener("change", function (event) {
      var input = event.target.closest(inputSelector);
      if (input && root.contains(input)) {
        syncVariant(input.value, input.checked);
        renderSelectedTray();
      }
      updatePriceSummary();
    });

    root.querySelectorAll(inputSelector).forEach(function (input) {
      syncVariant(input.value, input.checked);
    });
    renderSelectedTray();
    updatePriceSummary();

    if (!modal || !opener) return;

    function setOpen(open) {
      modal.hidden = !open;
      modal.classList.toggle("is-open", open);
      modal.setAttribute("aria-hidden", open ? "false" : "true");
      opener.setAttribute("aria-expanded", open ? "true" : "false");
      if (open) {
        lastFocus = document.activeElement;
        previousOverflow = document.documentElement.style.overflow;
        document.documentElement.style.overflow = "hidden";
        window.requestAnimationFrame(function () {
          var focusTarget = modal.querySelector(
            ".pdp-extras-dialog [data-pdp-extras-close]"
          );
          if (focusTarget) focusTarget.focus();
        });
      } else {
        document.documentElement.style.overflow = previousOverflow;
        if (lastFocus && typeof lastFocus.focus === "function") lastFocus.focus();
      }
    }

    opener.addEventListener("click", function (event) {
      event.preventDefault();
      setOpen(true);
    });
    modal.addEventListener("click", function (event) {
      var backdrop = event.target.closest("[data-pdp-extras-backdrop]");
      if (
        event.target.closest("[data-pdp-extras-close]") ||
        event.target === modal ||
        (backdrop && event.target === backdrop)
      ) {
        event.preventDefault();
        setOpen(false);
      }
    });
    document.addEventListener("keydown", function (event) {
      if (modal.hidden) return;
      if (event.key === "Escape") {
        setOpen(false);
        return;
      }
      if (event.key !== "Tab") return;

      var dialog = modal.querySelector(".pdp-extras-dialog");
      if (!dialog) return;
      var focusable = Array.prototype.slice.call(
        dialog.querySelectorAll("button, [href], input, select, textarea, [tabindex]")
      ).filter(function (element) {
        return !element.disabled && element.tabIndex >= 0 && !element.closest("[hidden]");
      });
      if (!focusable.length) return;

      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || !dialog.contains(document.activeElement))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (document.activeElement === last || !dialog.contains(document.activeElement))) {
        event.preventDefault();
        first.focus();
      }
    });

    var tabs = Array.prototype.slice.call(
      modal.querySelectorAll("[data-pdp-extras-tab]")
    );
    function selectTab(tab) {
      var targetId = tab.getAttribute("aria-controls") || tab.dataset.pdpExtrasTab;
      tabs.forEach(function (item) {
        var selected = item === tab;
        item.classList.toggle("is-active", selected);
        item.setAttribute("aria-selected", selected ? "true" : "false");
        item.setAttribute("tabindex", selected ? "0" : "-1");
      });
      modal.querySelectorAll("[data-pdp-extras-panel]").forEach(function (panel) {
        var panelId = panel.id || panel.dataset.pdpExtrasPanel;
        panel.hidden = String(panelId) !== String(targetId);
      });
    }
    tabs.forEach(function (tab, index) {
      tab.addEventListener("click", function () { selectTab(tab); });
      tab.addEventListener("keydown", function (event) {
        if (event.key !== "ArrowLeft" && event.key !== "ArrowRight") return;
        event.preventDefault();
        var offset = event.key === "ArrowRight" ? 1 : -1;
        var next = tabs[(index + offset + tabs.length) % tabs.length];
        selectTab(next);
        next.focus();
      });
    });
    var selectedTab = modal.querySelector('[data-pdp-extras-tab][aria-selected="true"]');
    if (selectedTab) selectTab(selectedTab);
  }

  /* One-vase offer: radio behaviour with the existing extra-card visuals. */
  function initVaseOffer(root) {
    var section = root.querySelector(".pdp-vase-offer");
    if (!section) return;
    var inputs = Array.prototype.slice.call(section.querySelectorAll("[data-pdp-vase-input]"));
    var modal = section.querySelector("[data-pdp-vase-modal]");
    var opener = section.querySelector("[data-pdp-vase-open]");
    var selectedTray = section.querySelector("[data-pdp-vase-selected]");
    var selectedId = null;
    var lastFocus = null;
    var oldOverflow = "";

    function render() {
      inputs.forEach(function (input) {
        input.checked = selectedId === input.value;
        input.disabled = false;
        var card = input.closest(".pdp-extra");
        if (card) {
          card.classList.toggle("is-selected", input.checked);
          card.classList.toggle("is-other", Boolean(selectedId && !input.checked));
          card.removeAttribute("aria-disabled");
        }
      });
      if (!selectedTray) return;
      selectedTray.innerHTML = "";
      var inline = section.querySelector('[data-pdp-vase-inline] [data-pdp-vase-input][value="' + selectedId + '"]');
      var popup = modal && modal.querySelector('[data-pdp-vase-input][value="' + selectedId + '"]');
      if (selectedId && !inline && popup) {
        var card = popup.closest(".pdp-extra").cloneNode(true);
        card.classList.remove("pdp-extra--popup");
        card.classList.add("pdp-extra--selected", "is-selected");
        selectedTray.appendChild(card);
      }
      selectedTray.hidden = !selectedTray.children.length;
    }

    section.addEventListener("change", function (event) {
      var input = event.target.closest("[data-pdp-vase-input]");
      if (!input) return;
      selectedId = input.checked ? input.value : null;
      render();
      // Let the existing total-price listener update after this selection settles.
      root.dispatchEvent(new Event("change", { bubbles: false }));
    });
    render();
    initVaseTimer(root, section);

    if (!modal || !opener) return;
    function setOpen(open) {
      modal.hidden = !open;
      modal.classList.toggle("is-open", open);
      modal.setAttribute("aria-hidden", open ? "false" : "true");
      opener.setAttribute("aria-expanded", open ? "true" : "false");
      if (open) {
        lastFocus = document.activeElement;
        oldOverflow = document.documentElement.style.overflow;
        document.documentElement.style.overflow = "hidden";
        var close = modal.querySelector("[data-pdp-vase-close]");
        if (close) close.focus();
      } else {
        document.documentElement.style.overflow = oldOverflow;
        if (lastFocus && lastFocus.focus) lastFocus.focus();
      }
    }
    opener.addEventListener("click", function () { setOpen(true); });
    modal.addEventListener("click", function (event) {
      if (event.target.closest("[data-pdp-vase-close]")) setOpen(false);
    });
    document.addEventListener("keydown", function (event) {
      if (modal.hidden) return;
      if (event.key === "Escape") { setOpen(false); return; }
      if (event.key !== "Tab") return;
      var focusable = Array.prototype.slice.call(modal.querySelectorAll("button, input, [href]")).filter(function (el) {
        return !el.disabled && !el.closest("[hidden]") && el.offsetParent !== null;
      });
      if (!focusable.length) return;
      var first = focusable[0], last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    });
  }

  /* Slim, date-based offer timer. End date/time are in Australia/Sydney. */
  function initVaseTimer(root, section) {
    var timer = section.querySelector("[data-pdp-vase-timer]");
    if (!timer) return;
    var output = timer.querySelector("[data-pdp-vase-timer-clock]");
    var date = timer.dataset.endDate || "";
    var time = timer.dataset.endTime || "23:59";
    var match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
    var clockMatch = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(time);
    if (!match || !clockMatch || !output || typeof Intl === "undefined") return;

    var year = +match[1], month = +match[2], day = +match[3];
    var hour = +clockMatch[1], minute = +clockMatch[2];
    var checkDate = new Date(Date.UTC(year, month - 1, day));
    if (checkDate.getUTCFullYear() !== year || checkDate.getUTCMonth() !== month - 1 || checkDate.getUTCDate() !== day) return;

    var formatter;
    try {
      formatter = new Intl.DateTimeFormat("en-GB", {
        timeZone: "Australia/Sydney", year: "numeric", month: "2-digit", day: "2-digit",
        hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23"
      });
    } catch (err) { return; }

    function localAsUtc(timestamp) {
      var fields = {};
      formatter.formatToParts(new Date(timestamp)).forEach(function (part) {
        if (part.type !== "literal") fields[part.type] = +part.value;
      });
      return Date.UTC(fields.year, fields.month - 1, fields.day, fields.hour, fields.minute, fields.second);
    }

    var desired = Date.UTC(year, month - 1, day, hour, minute, 0);
    var deadline = desired;
    for (var i = 0; i < 3; i++) deadline += desired - localAsUtc(deadline);

    // Never advertise a discounted offer after its configured end time.
    var expired = false;
    function update() {
      var seconds = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
      if (!seconds) {
        if (expired) return;
        expired = true;
        timer.hidden = true;
        section.querySelectorAll("[data-pdp-vase-input]").forEach(function (input) { input.checked = false; });
        section.style.display = "none";
        root.dispatchEvent(new Event("change"));
        clearInterval(interval);
        return;
      }
      timer.hidden = false;
      var days = Math.floor(seconds / 86400);
      var hours = Math.floor((seconds % 86400) / 3600);
      var minutes = Math.floor((seconds % 3600) / 60);
      var secs = seconds % 60;
      function pad(n) { return String(n).padStart(2, "0"); }
      output.textContent = (days ? days + "d " : "") + pad(hours) + "h " + pad(minutes) + "m " + pad(secs) + "s";
    }
    var interval;
    update();
    if (!expired) interval = setInterval(update, 1000);
  }

  /*
   * Same-day countdown. All math in Australia/Sydney local time so the message
   * is correct for shoppers in any timezone.
   *  - before cutoff on a delivery day: "Order in the next 2h 14m for same-day delivery"
   *  - after cutoff / non-delivery day:  merchant's after-cutoff message, with {day}
   *    replaced by the next day that appears in the block's delivery days
   *  - no usable delivery day at all:    merchant's static fallback message
   *
   * Day numbers are JS getDay() values (0=Sun … 6=Sat) — see dayMap below and the
   * matching Liquid render in sections/pdp-main.liquid. The shop day/time comes from
   * Intl in the Australia/Sydney zone; if that is unavailable we fall back to the
   * shop-time values Liquid rendered into the markup, never to the visitor's zone.
   */
  function initCountdown(root) {
    var el = root.querySelector("[data-pdp-countdown]");
    if (!el) return;
    var cutoff = (el.dataset.cutoff || "14:00").split(":");
    var cutoffMinutes = parseInt(cutoff[0], 10) * 60 + (parseInt(cutoff[1], 10) || 0);
    // Delivery days as JS day numbers, e.g. "1,2,3,4,5,6" = Mon-Sat.
    var days = (el.dataset.days || "1,2,3,4,5,6")
      .split(",")
      .map(function (value) { return parseInt(value, 10); })
      .filter(function (day) { return day >= 0 && day <= 6; });
    var dayNames = (el.dataset.dayNames || "Sunday,Monday,Tuesday,Wednesday,Thursday,Friday,Saturday").split(",");
    var before = el.dataset.textBefore || "Order in the next {time} for same-day delivery";
    var after = el.dataset.textAfter || "Order now for delivery on {day}";
    var afterFallback = el.dataset.textAfterFallback || "Order now for next-day delivery";
    // Shop-time snapshot from Liquid, advanced by the time elapsed since boot.
    var bootedAt = Date.now();
    var shopDay = parseInt(el.dataset.shopDay, 10);
    var shopMinutes = parseInt(el.dataset.shopMinutes, 10);

    function renderedShopNow() {
      if (isNaN(shopDay) || isNaN(shopMinutes)) return null;
      var total = shopMinutes + Math.floor((Date.now() - bootedAt) / 60000);
      return {
        day: (shopDay + Math.floor(total / 1440)) % 7,
        minutes: total % 1440,
      };
    }

    function sydneyNow() {
      try {
        var parts = new Intl.DateTimeFormat("en-AU", {
          timeZone: "Australia/Sydney",
          hour: "numeric",
          minute: "numeric",
          hour12: false,
          weekday: "short",
        }).formatToParts(new Date());
        var get = function (type) {
          return (parts.filter(function (p) { return p.type === type; })[0] || {}).value;
        };
        var dayMap = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
        var day = dayMap[get("weekday")];
        var minutes = parseInt(get("hour"), 10) * 60 + parseInt(get("minute"), 10);
        if (typeof day === "number" && !isNaN(minutes)) return { day: day, minutes: minutes };
      } catch (e) {
        /* no Intl / no tz database — fall through to the Liquid-rendered values */
      }
      return renderedShopNow();
    }

    // First delivery day strictly after `today`; null when no day qualifies.
    function nextDeliveryDay(today) {
      for (var offset = 1; offset <= 7; offset++) {
        var candidate = (today + offset) % 7;
        if (days.indexOf(candidate) !== -1) return dayNames[candidate];
      }
      return null;
    }

    function render() {
      var now = sydneyNow();
      if (!now) return; // keep the server-rendered text rather than guessing
      var deliveryDay = days.indexOf(now.day) !== -1;
      if (deliveryDay && now.minutes < cutoffMinutes) {
        var left = cutoffMinutes - now.minutes;
        var h = Math.floor(left / 60);
        var m = left % 60;
        var timeText = (h ? h + "h " : "") + m + "m";
        el.innerHTML = before.replace(
          "{time}",
          "<strong>" + timeText + "</strong>"
        );
      } else {
        var nextDay = nextDeliveryDay(now.day);
        el.textContent = nextDay ? after.replace("{day}", nextDay) : afterFallback;
      }
    }

    render();
    setInterval(render, 30000);
  }

  /* "Delivering to Suburb 2150 · Change" — wired to the RC location gate */
  function initLocationChip(root) {
    var chip = root.querySelector("[data-pdp-location]");
    if (!chip) return;
    var pill = document.getElementById("rc-pill");
    if (!pill) {
      chip.closest(".pdp-block").hidden = true; // gate disabled — hide the block
      return;
    }
    var labelEl = chip.querySelector("[data-pdp-location-label]");
    var btn = chip.querySelector("button");
    var actionEl = chip.querySelector("[data-pdp-location-action]");

    function refresh() {
      var pillLabel = document.getElementById("rc-pill-label");
      var pillPrefix = document.getElementById("rc-pill-prefix");
      var text = pillLabel ? pillLabel.textContent : "";
      var located = Boolean(pillPrefix && !pillPrefix.hidden && text);
      if (labelEl) labelEl.textContent = located ? text : "Set your delivery location";
      if (actionEl) actionEl.textContent = located ? "Change" : "Choose suburb";
    }

    btn.addEventListener("click", function () {
      pill.click();
    });
    document.addEventListener("rc:postcode-changed", function () {
      setTimeout(refresh, 400); // let the gate enrich suburb/state first
    });
    var pillLabel = document.getElementById("rc-pill-label");
    if (pillLabel && window.MutationObserver) {
      new MutationObserver(refresh).observe(pillLabel, {
        childList: true,
        characterData: true,
        subtree: true,
      });
    }
    var pillPrefix = document.getElementById("rc-pill-prefix");
    if (pillPrefix && window.MutationObserver) {
      new MutationObserver(refresh).observe(pillPrefix, {
        attributes: true,
        attributeFilter: ["hidden"],
      });
    }
    setTimeout(refresh, 600);
  }

  /* sticky mobile bar appears once the main buy row scrolls out of view */
  function initStickyBar(root) {
    var bar = root.querySelector(".pdp-sticky-bar");
    var buyRow = root.querySelector(".pdp-buy-row");
    if (!bar || !buyRow || !window.IntersectionObserver) return;
    new IntersectionObserver(
      function (entries) {
        bar.classList.toggle(
          "is-visible",
          !entries[0].isIntersecting && entries[0].boundingClientRect.top < 0
        );
      },
      { threshold: 0 }
    ).observe(buyRow);

    // Sticky button proxies the real form submit; the gate's capture click
    // handler on .btn-add-stickycart runs first and can block/preventDefault.
    var stickyBtn = bar.querySelector(".btn-add-stickycart");
    if (stickyBtn) {
      stickyBtn.addEventListener("click", function (event) {
        if (event.defaultPrevented) return; // gate opened its modal
        var form = root.querySelector('form[action="/cart/add"]');
        if (form) {
          if (form.requestSubmit) form.requestSubmit();
          else form.submit();
        }
      });
    }
  }

  /* single-path add to cart: main product + selected extras in one items[] call */
  function initAtc(root, product) {
    var form = root.querySelector('form[action="/cart/add"]');
    if (!form) return;
    var errorEl = root.querySelector(".pdp-buy-error");
    var isAdding = false;

    function showError(message) {
      if (!errorEl) return;
      errorEl.textContent = message || "";
      errorEl.hidden = !message;
    }

    form.addEventListener("submit", function (event) {
      if (event.defaultPrevented) return; // delivery gate blocked the submit
      event.preventDefault();
      if (isAdding) return;

      var atc = form.querySelector(".pdp-atc");
      var atcWasDisabled = atc ? atc.disabled : false;
      var idInput = form.querySelector('input[name="id"], select[name="id"]');
      var qtyInput = form.querySelector('input[name="quantity"]');
      var mainId = parseInt(idInput && idInput.value, 10);
      if (!Number.isFinite(mainId) || mainId <= 0) {
        showError("Please select an available option.");
        return;
      }
      var items = [
        {
          id: mainId,
          quantity: Math.max(1, parseInt(qtyInput && qtyInput.value, 10) || 1),
        },
      ];
      var selectedExtras = {};
      root.querySelectorAll(".pdp-extra input:checked, [data-pdp-extra-input]:checked").forEach(function (extra) {
        var id = parseInt(extra.value, 10);
        if (Number.isFinite(id) && id > 0 && id !== mainId) selectedExtras[id] = true;
      });
      Object.keys(selectedExtras).forEach(function (id) {
        items.push({ id: parseInt(id, 10), quantity: 1 });
      });

      isAdding = true;
      if (atc) atc.disabled = true;
      if (atc) atc.classList.add("is-adding");
      showError("");

      var routesRoot = (window.Shopify && Shopify.routes && Shopify.routes.root) || "/";
      fetch(routesRoot + "cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: items }),
      })
        .then(function (res) {
          if (!res.ok) {
            return res.json().then(function (data) {
              throw new Error(data.description || data.message || "Couldn't add to cart.");
            });
          }
          return res.json();
        })
        .then(function () {
          if (window.flowerMiniCart && typeof window.flowerMiniCart.refresh === "function") {
            return window.flowerMiniCart.refresh().then(function () {
              window.flowerMiniCart.open();
            }).catch(function (refreshError) {
              console.error(refreshError);
              window.flowerMiniCart.open();
            });
          }
          if (typeof ajaxCart !== "undefined" && ajaxCart.load) {
            ajaxCart.load();
            var miniCart = document.querySelector(".js-minicart");
            var miniCartBackdrop = document.querySelector(".bg-minicart");
            if (miniCart) miniCart.classList.add("active");
            if (miniCartBackdrop) miniCartBackdrop.classList.add("active");
            if (
              typeof timber !== "undefined" &&
              timber.RightDrawer &&
              timber.RightDrawer.open
            ) {
              timber.RightDrawer.open();
            }
          } else {
            window.location.href = routesRoot + "cart";
          }
        })
        .catch(function (err) {
          showError(err.message || "Couldn't add to cart. Please try again.");
        })
        .finally(function () {
          isAdding = false;
          if (atc) atc.disabled = atcWasDisabled;
          if (atc) atc.classList.remove("is-adding");
        });
    });
  }

  /* ---------------- pdp-similar (recommendations) ---------------- */

  function initSimilar(root) {
    var track = root.querySelector(".pdp-similar-track");
    if (!track) return;

    root.querySelectorAll(".pdp-carousel-nav button").forEach(function (btn) {
      btn.addEventListener("click", function () {
        var card = track.querySelector(".pdp-card");
        var step = card ? card.offsetWidth + 16 : track.clientWidth / 2;
        track.scrollBy({
          left: step * 2 * (btn.dataset.dir === "next" ? 1 : -1),
          behavior: "smooth",
        });
      });
    });

    var url = root.dataset.recommendationsUrl;
    if (!url) return; // manual collection mode — server-rendered
    fetch(url)
      .then(function (res) {
        return res.ok ? res.text() : null;
      })
      .then(function (html) {
        if (!html) return;
        var doc = new DOMParser().parseFromString(html, "text/html");
        var fresh = doc.querySelector(".pdp-similar-track");
        if (fresh && fresh.children.length) {
          track.innerHTML = fresh.innerHTML;
        } else {
          var section = root.closest(".shopify-section") || root;
          if (!track.children.length) section.style.display = "none";
        }
      })
      .catch(function () {});
  }

  /* ---------------- boot ---------------- */

  function boot() {
    document.querySelectorAll(".pdp").forEach(initSection);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }

  // Theme editor: sections re-render on setting changes.
  document.addEventListener("shopify:section:load", function (event) {
    var el = event.target.querySelector(".pdp");
    if (el) {
      delete el.dataset.pdpReady;
      initSection(el);
    }
  });
})();
